import { useCallback, useMemo } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { ReleaseType } from '@ibk/shared-types';
import { useHappyHolidaysEndOfTheYearRecharge } from '../../../hooks';
import { getProductName, getRechargeTypeLabel } from '../../../utils/voucherUtils';
import { NewRechargeStep } from '../types';
import { createBeneficiaryValueFormatterService, createDateFormatterService, createDiscountFormatterService, } from './utils/stepDataFormatters';
export function useStepDataFormatter(formData, product) {
    const { t } = useTranslation();
    const dateFormatterService = useMemo(() => createDateFormatterService(), []);
    const discountFormatterService = useMemo(() => createDiscountFormatterService(t), [t]);
    const beneficiaryValueFormatterService = useMemo(() => createBeneficiaryValueFormatterService(t), [t]);
    const { isInPeriod } = useHappyHolidaysEndOfTheYearRecharge();
    const getCompetenceData = useCallback(() => {
        return {
            competence: formData?.competenceDate
                ? dateFormatterService.formatCompetenceDate(formData.competenceDate)
                : undefined,
            rechargeType: getRechargeTypeLabel(formData?.rechargeType, isInPeriod, t),
            product: formData?.voucherType ? getProductName(formData.voucherType, t) : undefined,
            name: formData?.rechargeIdentification,
        };
    }, [
        formData?.competenceDate,
        formData?.rechargeType,
        formData?.voucherType,
        formData?.rechargeIdentification,
        dateFormatterService,
        t,
        isInPeriod,
    ]);
    const getGlobalSettingsData = useCallback(() => {
        const globalValue = formData?.globalValue;
        let formattedGlobalValue;
        if (globalValue) {
            const suffix = formData?.releaseType === ReleaseType.MONTHLY
                ? t('recharge.value.suffix.monthly')
                : t('recharge.value.suffix.daily');
            formattedGlobalValue = `${globalValue} ${suffix}`;
        }
        return {
            creditDate: formData?.creditDate ? dateFormatterService.formatCreditDate(formData.creditDate) : undefined,
            calculation: beneficiaryValueFormatterService.formatCalculationType(formData?.releaseType),
            globalValue: formattedGlobalValue,
            businessDays: formData?.businessDays,
            discount: discountFormatterService.formatDiscount(formData?.discountType, formData?.discountValue, formData?.payrollDeduction),
        };
    }, [
        formData?.creditDate,
        formData?.discountType,
        formData?.discountValue,
        formData?.payrollDeduction,
        dateFormatterService,
        discountFormatterService,
        beneficiaryValueFormatterService,
        formData?.releaseType,
        formData?.globalValue,
        formData?.businessDays,
        t,
    ]);
    const getBenefitsValuesData = useCallback(() => {
        return {
            calculation: beneficiaryValueFormatterService.formatCalculationType(formData?.releaseType),
            beneficiaries: formData?.selectedBeneficiaries ? formData.selectedBeneficiaries.toString() : undefined,
            value: beneficiaryValueFormatterService.formatBeneficiaryValue(formData?.beneficiaries, formData?.globalValue, product, formData?.releaseType),
        };
    }, [
        formData?.beneficiaries,
        formData?.selectedBeneficiaries,
        formData?.releaseType,
        formData?.globalValue,
        product,
        beneficiaryValueFormatterService,
    ]);
    const getStepData = useCallback((step) => {
        switch (step) {
            case NewRechargeStep.COMPETENCE:
                return getCompetenceData();
            case NewRechargeStep.GLOBAL_SETTINGS:
                return getGlobalSettingsData();
            case NewRechargeStep.BENEFICIARIES_VALUES:
                return getBenefitsValuesData();
            case NewRechargeStep.DELIVERY_ADDRESS:
                return {
                    deliveryMethod: formData?.deliveryMethod,
                    address: formData?.deliveryAddress,
                };
            case NewRechargeStep.PAYMENT:
                return {
                    paymentMethod: formData?.paymentMethod,
                    totalAmount: formData?.totalAmount,
                };
            default:
                return {};
        }
    }, [getCompetenceData, getGlobalSettingsData, getBenefitsValuesData, formData]);
    return {
        getStepData,
    };
}
